import TokenService from '#services/token';
import * as locationService from '#services/location';
import axios from 'axios';
import { server } from '#config';
import { unstable_batchedUpdates } from 'react-dom';
import { useUIStore } from '#store';

const instance = axios.create({
  baseURL: server,
  timeout: 15000,
});

instance.interceptors.request.use((config) => {
  const token = TokenService.getLocalAccessToken();
  if (token) {
    config.headers['Authorization'] = 'Bearer ' + token;
  }
  const sessionId = useUIStore.getState().sessionInfo?.id;
  if (sessionId) {
    config.headers['X-Session-Id'] = sessionId;
  }
  return config;
});

instance.interceptors.response.use(
  (res) => {
    return res;
  },
  async (err) => {
    const originalConfig = err.config;
    if (err?.response?.status === 401 || err?.response?.status === 403) {
      // we are in auth related issue category
      if (originalConfig.url === '/auth/refresh-token') {
        // refresh-token request has failed
        await logoutExpiredUser();
        // No need to even resolve/reject the promise
      } else if (originalConfig.url !== '/auth/google') {
        if (err?.response?.data?.failure?.message === 'jwt expired') {
          // The JWT has expired fetch a new token
          const user = TokenService.getUser();
          if (!user || !user._id) {
            // No user found, log the user out (redirect handles the UI) and
            // resolve a failure-shaped response so callers don't crash.
            await logoutExpiredUser();
            return failureResponse(err.response, 'Session expired');
          }
          originalConfig._retry = true;
          try {
            const rs = await instance.post('/auth/refresh-token', {
              refreshToken: TokenService.getLocalRefreshToken(),
              _id: user._id,
            });
            const token = rs.data?.success;
            TokenService.setToken(token);
            return instance(originalConfig);
          } catch (_error) {
            return failureResponse(_error?.response, 'Session expired');
          }
        } else if (
          err.response?.data?.failure?.message === 'No auth token' ||
          err.response?.data?.failure?.message ===
            'Incorrect email or refreshToken' ||
          err.response?.data?.failure?.message ===
            'Expired refresh token'
        ) {
          // We should log out the user
          logoutExpiredUser();
        }
      }
      return failureResponse(err.response, 'Unauthorized');
    } else {
      if (err?.response) {
        return Promise.resolve(err.response);
      }
      // Network error, timeout, or request aborted (e.g. page unload).
      // Without this, callers that do `const resp = await api.x(); return resp.data;`
      // crash with "Cannot read properties of undefined (reading 'data')",
      // and downstream `{ success, failure } = await api.x()` destructures throw.
      // Synthesize a response-shaped object so the existing failure path is taken.
      const code = err?.code;
      const message =
        code === 'ECONNABORTED'
          ? 'Request timed out'
          : code === 'ERR_CANCELED'
          ? 'Request canceled'
          : err?.message || 'Network error';
      return Promise.resolve({
        status: 0,
        data: { failure: { message, code: code || 'NETWORK_ERROR' } },
      });
    }
  }
);

/**
 * Always resolve a `{ status, data: { failure } }`-shaped response so every
 * `const resp = await instance.x()` (then `resp.data`) and every downstream
 * `const { success, failure } = await api.x()` stays defined instead of
 * throwing "Cannot read properties of undefined". Reuses the real response's
 * `data.failure` / status when present (e.g. a genuine 401/403 body), and
 * synthesizes one when `response` is undefined (network error during retry).
 */
function failureResponse(response, fallbackMessage) {
  const existingFailure = response?.data?.failure;
  return Promise.resolve({
    status: response?.status ?? 401,
    data: {
      failure: existingFailure || {
        message: fallbackMessage || 'Request failed',
        code: 'AUTH_ERROR',
      },
    },
  });
}

const logoutExpiredUser = () => {
  unstable_batchedUpdates(async () => {
    await useUIStore.getState().logoutUser();
    window.location.href = "/?session=expired";
  });
};

export const getSuggestions = async ({ query, mode }) => {
  const params = new URLSearchParams({ q: query });
  if (mode) params.append('mode', mode);
  const resp = await instance.get(`/search?${params.toString()}`);
  return resp.data;
};

export const createTracker = async ({ tracker, outing }) => {
  const resp = await instance.post(`/trackers`, { tracker, outing });
  return resp.data;
};

export const oAuthLogin = async ({ credential }) => {
  const resp = await instance.post(`/auth/google`, {
    token: credential,
  });
  return resp.data;
};

export const registerContact = async ({ contact }) => {
  const resp = await instance.post(`/contact/register`, contact);
  return resp.data;
};

export const verifyContact = async ({ contact, code }) => {
  const resp = await instance.post(`/contact/verify`, { ...contact, code });
  return resp.data;
};

// ************* PAYMENT **************** //

export const getPromotion = async ({ promotionCode }) => {
  const resp = await instance.get(`/payments/promotions/check?promotionCode=${promotionCode}`);
  return resp.data;
};

// export const createPayment = async ({
//   token,
//   planSlug,
//   address,
//   locationId,
//   verificationToken,
//   paymentMode,
// }) => {
//   const body = {
//     payment: {
//       locationId,
//       sourceId: token,
//       verificationToken,
//     },
//     planSlug,
//     address,
//     paymentMode,
//   };
//   try {
//     const resp = await instance.post('/payments/create', body);
//     if (!resp) {
//       return { failure: { message: "Can't verify the payment" } };
//     }
//     return resp.data;
//   } catch (err) {
//     return err.response.data;
//   }
// };

export const createPaymentIntent = async ({ planSlug, currency, promotionCode }) => {
  const body = {
    planSlug,
    currency,
    promotionCode,
  };
  const resp = await instance.post('/payments/intent', body, {
    timeout: 30000, // 30 seconds timeout for payment processing
  });
  return resp.data;
};

export const subscribe = async ({
  token,
  planSlug,
  address,
  locationId,
  verificationToken,
  paymentMode,
}) => {
  const body = {
    payment: {
      locationId,
      // sourceId: process.env.REACT_APP_PUBLIC_CARD_NONCE
      //   ? process.env.REACT_APP_PUBLIC_CARD_NONCE
      //   : token,
      sourceId: token,
      verificationToken,
    },
    planSlug,
    address,
    paymentMode,
  };
  try {
    const resp = await instance.post('/payments/subscribe', body);
    return resp.data;
  } catch (err) {
    return err.response.data;
  }
};

export const createSub = async ({ sub }) => {
  const resp = await instance.post(`/subscriptions`, { sub });
  return resp.data;
};

export const createCodeSub = async ({ code, planSlug }) => {
  const resp = await instance.post(`/payments/code`, { code, planSlug });
  return resp.data;
};

// ************* TRACKER **************** //

export const getTrackers = async ({
  page = 0,
  size = 100,
  sortBy = 'createdAt',
  order = 'desc',
  adminView = false,
  status,
}) => {
  let url = `/trackers?page=${page}&size=${size}&adminView=${adminView}&sortBy=${sortBy}&order=${order}`;
  if (status) url += `&status=${status}`;
  const resp = await instance.get(url);
  return resp.data;
};

export const getTracker = async ({ shortId }) => {
  const resp = await instance.get(`/trackers/short/${shortId}`);
  return resp.data;
};

export const getNotifications = async () => {
  const resp = await instance.get(`/trackers/notifications`);
  return resp.data;
};

// ************* TRACKER **************** //
export const getSub = async () => {
  const resp = await instance.get(`/subscriptions`);
  return resp.data;
};

export const getUser = async () => {
  const resp = await instance.get(`/auth`);
  return resp.data;
};

export const getPlans = async ({ country }) => {
  const resp = await instance.get(`/plans/all?country=${country}`);
  return resp.data;
};

export const updateTracker = async ({ tracker, notes }) => {
  const resp = await instance.put(`/trackers`, {
    tracker,
    ...(notes ? { notes } : {}),
  });
  return resp.data;
};

export const updateTrackerStatus = async ({ tracker, notes }) => {
  const resp = await instance.put(`/trackers`, {
    tracker,
    ...(notes ? { notes } : {}),
  });
  return resp.data;
};

export const updateTrackerShortStatus = async ({ shortId, booked }) => {
  const resp = await instance.put(`/trackers/short/${shortId}`, {
    booked,
  });
  return resp.data;
};

export const getGiftCards = async () => {
  const resp = await instance.get(`/giftcards`);
  return resp.data;
};

export const updateSubStatus = async ({
  subscription,
  period,
  paymentId,
  customPayment,
}) => {
  const resp = await instance.post(`/subscriptions/update`, {
    subscription,
    period,
    paymentId,
    customPayment,
  });
  return resp.data;
};

export const getUpgradePreview = async ({ subscriptionId }) => {
  const resp = await instance.get(
    `/subscriptions/upgrade-preview?subscriptionId=${subscriptionId}`
  );
  return resp.data;
};

export const upgradeSubscription = async ({ subscriptionId }) => {
  const resp = await instance.post(`/subscriptions/upgrade`, {
    subscriptionId,
  });
  return resp.data;
};

export const getCampsites = async ({ outingId }) => {
  const resp = await instance.get(`/campsites?outingId=${outingId}`);
  return resp.data;
};

export const getOuting = async ({ slug }) => {
  const resp = await instance.get(`/outings?slug=${slug}`);
  return resp.data;
};

export const getOutingStats = async ({ outingId, entityKey }) => {
  const params = new URLSearchParams({ outingId });
  if (entityKey) params.append('entityKey', entityKey);
  const resp = await instance.get(`/outings/stats?${params.toString()}`);
  return resp.data;
};

export const getOutingAvailability = async ({
  slug,
  startDate,
  endDate,
  groupSize,
  campsiteIds,
}) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  if (groupSize) params.append('groupSize', String(groupSize));
  if (campsiteIds?.length) {
    params.append('campsiteIds', campsiteIds.join(','));
  }
  const queryString = params.toString();
  const url = `/outings/${slug}/availability` + (queryString ? `?${queryString}` : '');
  const resp = await instance.get(url, {
    timeout: 120000, // 2 minutes timeout for availability endpoint
  });
  return resp.data;
};

export const getFlickrPhotoIds = async ({ text, limit = 10 }) => {
  let textToSearch = text;
  let resp = await axios.get(
    `https://www.flickr.com/services/rest/?method=flickr.photos.search&api_key=${process.env.REACT_APP_FLICKR_API_KEY}&text=${textToSearch}&format=json&nojsoncallback=1&geo_context=0&per_page=${limit}&orientation=landscape&sort=relevance`
  );
  if ((resp?.data?.photos?.total ?? 0) === 0) {
    // Shorten the text if no results
    textToSearch = text.split(' ').slice(0, 2).join(' ');
    textToSearch = textToSearch.replace(/[^a-zA-Z ]/g, '');
  }
  resp = await axios.get(
    `https://www.flickr.com/services/rest/?method=flickr.photos.search&api_key=${process.env.REACT_APP_FLICKR_API_KEY}&text=${textToSearch}&format=json&nojsoncallback=1&geo_context=0&per_page=${limit}&orientation=landscape&sort=relevance`
  );
  return resp.data;
};

export const getNearbyOutings = async ({ parentName, reservable }) => {
  const resp = await instance.get(
    `/outings?parentName=${parentName}&reservable=${reservable}`
  );
  return resp.data;
};

export const getStateOutings = async ({ state }) => {
  const resp = await instance.get(`/outings/state/${state}`);
  return resp.data;
};

export const getBlog = async ({ slug }) => {
  const resp = await instance.get(`/blogs/?slug=${slug}`);
  return resp.data;
};

export const getBlogs = async ({ page, size }) => {
  const resp = await instance.get(`/blogs/?page=${page}&size=${size}`);
  return resp.data;
};

export const upsertBlog = async ({ blog }) => {
  const resp = await instance.post(`/blogs`, { blog });
  return resp.data;
};

// ************* ADMIN **************** //

export const adminLogin = async ({ email, id, password }) => {
  const resp = await instance.post(`/auth/admin-login`, {
    email,
    id,
    password,
  });
  return resp.data;
};

export const generateSitemap = async () => {
  const resp = await instance.get(`/admin/generate-sitemap`);
  return resp.data;
};

export const getDashData = async (params = {}) => {
  const resp = await instance.get(`/admin/dashboard`, { params });
  return resp.data;
};

export const getAdTraffic = async (params = {}) => {
  const resp = await instance.get(`/admin/ad-traffic`, { params, timeout: 30000 });
  return resp.data;
};

export const claimAdAttribution = async (attribution) => {
  const resp = await instance.post(`/users/ad-attribution`, attribution);
  return resp.data;
};

export const getDashboardStripePayingNzAu = async () => {
  const resp = await instance.get(`/admin/dashboard/stripe-paying-nz-au`);
  return resp.data;
};

export const getFunnelData = async (params = {}) => {
  const resp = await instance.get(`/admin/funnel`, { params });
  return resp.data;
};

export const getRecReview = async () => {
  const resp = await instance.get(`/admin/rec-review`, {
    timeout: 20 * 60 * 1000,
  });
  return resp.data;
};

export const updatedSafeOutings = async () => {
  const resp = await instance.get(`/admin/rec-update-safe`, {
    timeout: 20 * 60 * 1000,
  });
  return resp.data;
};

export const saveRecReview = async ({ outing, recOuting }) => {
  const resp = await instance.post(`/admin/rec-review`, {
    outing,
    recOuting,
  });
  return resp.data;
};

/** Search recreation.gov; returns results with alreadyInDb. Params: { q } or { entity_id } */
export const getRecSearch = async (params = {}) => {
  const resp = await instance.get(`/admin/rec-search`, { params, timeout: 20000 });
  return resp.data;
};

/** Add a single outing from rec.gov. Body: { entity_id } or { outing } */
export const addRecOuting = async (body) => {
  const resp = await instance.post(`/admin/rec-add-outing`, body, { timeout: 30000 });
  return resp.data;
};

// updates the campsites table
export const updatedSafeCamps = async () => {
  const resp = await instance.post(
    `/admin/camp-review`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const updatedSafePermits = async () => {
  const resp = await instance.post(
    `/admin/permit-review`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const getCampReview = async () => {
  const resp = await instance.get(`/admin/camp-review`, {
    timeout: 20 * 60 * 1000,
  });
  return resp.data;
};

export const updatePermalinks = async () => {
  const resp = await instance.post(
    `/admin/update-links`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const addYSLodges = async () => {
  const resp = await instance.post(
    `/admin/ys-init`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const addGCLodges = async () => {
  const resp = await instance.post(
    `/admin/gc-init`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const syncTourSlots = async () => {
  const resp = await instance.post(
    `/admin/tour-slots-update-safe`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const syncBrevo = async () => {
  const resp = await instance.post(
    `/admin/sync-brevo`,
    {},
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const updatePermit = async ({ permit, crawlerType }) => {
  const resp = await instance.post(
    `/admin/permit-mapping`,
    {
      permit,
      crawlerType,
    },
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const getPermitMapping = async ({ page, size }) => {
  const resp = await instance.get(
    `/admin/permit-mapping?page=${page}&size=${size}`,
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const getTrackerLimitExceptions = async () => {
  const resp = await instance.get(`/admin/tracker-limit-exceptions`);
  return resp.data;
};

export const addTrackerLimitException = async ({ email, note }) => {
  const resp = await instance.post(`/admin/tracker-limit-exceptions`, {
    email,
    note: note ?? '',
  });
  return resp.data;
};

export const removeTrackerLimitException = async (userId) => {
  const resp = await instance.delete(
    `/admin/tracker-limit-exceptions/${userId}`
  );
  return resp.data;
};


export const getAdminNotifications = async ({ type = 'admin', limit = 50 } = {}) => {
  const resp = await instance.get(`/admin/notifications`, {
    params: { type, limit },
  });
  return resp.data;
};

export const getAdminSmsDashboard = async ({ limit = 25 } = {}) => {
  const resp = await instance.get(`/admin/sms-dashboard`, {
    params: { limit },
    timeout: 30000,
  });
  return resp.data;
};

export const markAdminNotificationRead = async (id) => {
  const resp = await instance.patch(`/admin/notifications/${id}/read`);
  return resp.data;
};

export const markAllAdminNotificationsRead = async ({ type = 'admin' } = {}) => {
  const resp = await instance.post(`/admin/notifications/mark-all-read`, { type });
  return resp.data;
};

export const updateOffer = async ({ emails }) => {
  const resp = await instance.post(
    `/admin/update-offer`,
    {
      type: 'YEAROFF',
      emails,
    },
    {
      timeout: 20 * 60 * 1000,
    }
  );
  return resp.data;
};

export const getOfferUsers = async () => {
  const resp = await instance.get(`/admin/offer-users`);
  return resp.data;
};

export const addOfferUser = async ({ email, note }) => {
  const resp = await instance.post(
    `/admin/update-offer`,
    { email, note: note ?? '' },
    { timeout: 5 * 60 * 1000 }
  );
  return resp.data;
};

export const removeOfferUser = async (email) => {
  const resp = await instance.delete(
    `/admin/offer-users/${encodeURIComponent(email)}`
  );
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* EVENTS **************** //

export const postFeedback = async ({ description, name, tracker, user }) => {
  const resp = await instance.post(`/feedbacks`, {
    description,
    name,
    tracker,
    user,
  });
  return resp.data;
};

export const getFeedbacks = async () => {
  const resp = await instance.get(`/feedbacks`);
  return resp.data;
};

export const getFeedbacksForBlog = async (blogSlug) => {
  const resp = await instance.get(`/feedbacks/blog`, {
    params: { blogSlug }
  });
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* EVENTS **************** //

export const putEvent = async ({ type, data, sessionId }) => {
  const resp = await instance.put(`/events`, {
    type,
    data,
    sessionId,
  });
  return resp.data;
};

export const backfillEvents = async ({ sessionId }) => {
  const resp = await instance.post(`/events/backfill`, {
    sessionId,
  });
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* STATS **************** //

// NOT USED - keeping for potential future use
export const getStat = async ({ type }) => {
  const resp = await instance.get(`/stats?type=${type}`);
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* USER **************** //

export const updateUserPref = async ({
  preference,
  userId,
  email,
  address,
  firstName,
  lastName,
  secondaryPhone,
}) => {
  const resp = await instance.patch(`/users/${userId}`, {
    preference,
    email,
    address,
    firstName,
    lastName,
    secondaryPhone,
  });
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

export const getAffiliate = async ({ userId }) => {
  const resp = await instance.post(`/affiliate`);
  return resp.data;
};

export const getCurrentLocation = () => locationService.getCurrentLocation();

export const getStateRecs = async ({ stateCode }) => {
  const resp = await instance.get(`/recoms/states/${stateCode}`);
  return resp.data;
};

// ************* SCAN **************** //

export const getScans = async ({ trackerId, page, size }) => {
  let params = `page=${page}&size=${size}`;
  if (trackerId) {
    params = `${params}&trackerId=${trackerId}`;
  }
  const resp = await instance.get(`/scans?${params}`);
  return resp.data;
};

export const getAlerts = async ({ trackerId, page, size }) => {
  let params = `page=${page}&size=${size}`;
  if (trackerId) {
    params = `${params}&trackerId=${trackerId}`;
  }
  const resp = await instance.get(`/alerts?${params}`);
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* CHAT **************** //

export const chat = async ({ messages }) => {
  const resp = await instance.post(`/support/chat`, { messages });
  return resp.data;
};

// ************* FEED LEADS **************** //

export const getFeedLeads = async () => {
  const resp = await instance.get(`/feed-leads`);
  return resp.data;
};

export const getFeedLeadsStats = async () => {
  const resp = await instance.get(`/feed-leads/stats`);
  return resp.data;
};

export const updateFeedLead = async (leadId, updateData) => {
  const resp = await instance.put(`/feed-leads/${leadId}`, updateData);
  return resp.data;
};

// ************* AI SERVICES **************** //

export const analyzeTrailNameWithAI = async (trailName, location, description) => {
  const resp = await instance.post(`/ai/analyze-trail-name`, {
    trailName,
    location,
    description,
  });
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* LEAD MAGNETS **************** //

export const subscribeToLeadMagnet = async ({ email, source, pageSlug, pageType, pageUrl, metadata }) => {
  const resp = await instance.post(`/leads/subscribe`, {
    email,
    source,
    pageSlug,
    pageType,
    pageUrl,
    metadata,
  });
  return resp.data;
};

export const validateDownloadToken = async (token) => {
  const resp = await instance.get(`/leads/download/${token}`);
  return resp.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //

// ************* TRAIL / WONDERLAND ITINERARY PLANNER **************** //

/** Client-side cache so ±1 month availability isn't re-fetched while planning. */
const trailAvailCache = new Map();

function trailCacheCovers(entry, from, to, groupSize) {
  if (!entry) return false;
  if (Number(entry.groupSize) !== Number(groupSize)) return false;
  return entry.fromDate <= from && entry.toDate >= to;
}

export const getTrailAvailability = async ({
  trailId,
  from,
  to,
  groupSize = 1,
  force = false,
}) => {
  const key = `${trailId}|${from}|${to}|${groupSize}`;
  if (!force) {
    const exact = trailAvailCache.get(key);
    if (exact) return exact;
    for (const [cacheKey, entry] of trailAvailCache.entries()) {
      if (!cacheKey.startsWith(`${trailId}|`)) continue;
      if (trailCacheCovers(entry, from, to, groupSize)) return entry;
    }
  }
  const params = new URLSearchParams({
    from,
    to,
    groupSize: String(groupSize),
  });
  const resp = await instance.get(`/trips/${trailId}/availability?${params}`, {
    timeout: 120000,
  });
  const data = resp.data?.success?.data;
  if (data) trailAvailCache.set(key, data);
  return data;
};

/** Trip-mode track + curated huts for a planner trail (deterministic, not search). */
export const getTrailTrack = async ({ trailId }) => {
  const resp = await instance.get(`/trips/${trailId}/track`);
  return resp.data?.success?.data;
};

export const getWonderlandAvailability = async ({
  from,
  to,
  groupSize = 1,
  force = false,
}) =>
  getTrailAvailability({
    trailId: 'wonderland',
    from,
    to,
    groupSize,
    force,
  });

export const getWonderlandNextCamps = async (body) => {
  const resp = await instance.post(`/trips/wonderland/next`, body, {
    timeout: 60000,
  });
  return resp.data?.success?.data;
};

// XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX //
